<template>
  <div class="archive__payment-form">
    <div class="archive__email-input-container">
      <label
        for="payment-email"
        class="archive__email-label archive__email-label--flex"
      >
        <div class="archive__email-label-text">Your email address</div>

        <label class="archive__gift-toggle">
          <input
            v-model="isGift"
            type="checkbox"
            class="archive__gift-checkbox"
            @change="handleGiftToggle"
          />
          <span>This is a gift</span>
        </label>
      </label>
      <input
        id="payment-email"
        v-model="email"
        type="email"
        class="archive__email-input"
        placeholder="you@example.com"
        @blur="handleEmailChange"
      />
    </div>
    <div v-if="isGift" class="archive__email-input-container">
      <label for="giftee-email" class="archive__email-label">
        The recipient's email address
      </label>
      <input
        id="giftee-email"
        v-model="giftRecipientEmailAddress"
        type="email"
        class="archive__email-input"
        placeholder="recipient@example.com"
        @blur="handlegiftRecipientEmailAddressChange"
      />
    </div>
    <div v-if="isGift" class="archive__email-input-container">
      <label for="gift-message" class="archive__email-label">
        Gift message (optional)
      </label>
      <textarea
        id="gift-message"
        v-model="giftMessage"
        class="archive__email-input"
        placeholder="Add a personal message..."
        rows="3"
        @blur="handleGiftMessageChange"
      ></textarea>
    </div>
    <div
      id="payment-element"
      class="archive__payment-element"
      :class="{ 'archive__payment-element--mounting': isMounting }"
    ></div>
    <button
      id="pay-button"
      type="button"
      class="green archive__button archive__subscribe-form-button"
      :class="{ pending: isPending }"
      :disabled="isButtonDisabled || isPending"
    >
      <div class="archive__pay-button-content">
        {{ formattedPrice ? `Subscribe for ${formattedPrice}` : "Subscribe" }}
      </div>
    </button>
  </div>

  <Teleport to="body">
    <div
      v-if="showSuccessModal"
      class="archive__modal-overlay"
      @click="showSuccessModal = false"
    >
      <div class="archive__modal-container" @click.stop>
        <div class="archive__modal-header">
          <h2 class="archive__modal-title">Welcome to Premium!</h2>
          <button
            type="button"
            class="archive__modal-close"
            @click="showSuccessModal = false"
          >
            <CloseIcon class="archive__close-icon" />
          </button>
        </div>
        <div class="archive__modal-content">
          <CheckIcon class="archive__success-icon" />
          <p class="archive__success-message">
            You've successfully upgraded to premium! Get started by exploring
            the archives or managing your subscription.
          </p>
        </div>
        <div class="archive__modal-actions">
          <button
            type="button"
            class="archive__success-button archive__success-button--secondary"
            @click="showSuccessModal = false"
          >
            Close
          </button>
          <a
            :href="archiveUrl"
            class="archive__success-button archive__success-button--primary"
          >
            <ArchiveIcon class="archive__button-icon" />
            View Archives
          </a>
        </div>
      </div>
    </div>
  </Teleport>
</template>

<script lang="ts" setup>
import { computed, onMounted, onUnmounted, ref } from "vue";

import ArchiveIcon from "@/icons/heroicons/archive-box-micro.svg";
import CheckIcon from "@/icons/heroicons/check-circle-micro.svg";
import CloseIcon from "@/icons/heroicons/x-mark-micro.svg";
import { Newsletter } from "@/types/newsletter";

declare global {
  interface Window {
    Stripe?: any;
  }
}

const getAppearance = () => {
  const isDarkMode = document.documentElement.classList.contains("dark");

  if (!isDarkMode) {
    return {
      theme: "flat",
      inputs: "condensed",
      labels: "floating",
      layout: {
        type: "tabs",
        inputs: "spaced",
        labels: "hidden",
      },
      variables: {
        fontLineHeight: "1.5",
        borderRadius: "20px",
        colorBackground: "#ffffff",
        accessibleColorOnColorPrimary: "#1f2937",
        spacingUnit: 8,
        tabSpacing: "5px",
      },
      rules: {
        ".Input": {
          backgroundColor: "#ffffff",
          padding: "6px 12px",
          border: "1px solid #d1d5db",
          color: "#111827",
          fontSize: "14px",
        },
        ".Input:disabled, .Input--invalid:disabled": {
          color: "#9ca3af",
          backgroundColor: "#f9fafb",
        },
        ".Tab": {
          padding: "10px 12px 8px 12px",
          border: "1px solid #d1d5db",
          backgroundColor: "#ffffff",
          color: "#111827",
        },
        ".Tab:hover": {
          backgroundColor: "#f9fafb",
          color: "#111827",
        },
        ".TabIcon": {
          width: "16px",
          fill: "#6b7280",
        },
        ".TabIcon:hover": {
          fill: "#111827",
        },
        ".Tab--selected, .Tab--selected:focus, .Tab--selected:hover": {
          border: "none",
          backgroundColor: "#007bfd",
          color: "#ffffff",
          boxShadow:
            "0 0 0 1.5px var(--colorPrimaryText), 0px 1px 1px rgba(0, 0, 0, 0.03), 0px 3px 7px rgba(18, 42, 66, 0.04)",
        },
        ".TabIcon--selected,.TabIcon--selected:hover,.TabIcon--selected:focus":
          {
            fill: "#ffffff",
          },
        ".Label": {
          fontSize: "14px",
          color: "#6b7280",
        },
      },
    };
  }

  return {
    theme: "flat",
    inputs: "condensed",
    labels: "floating",
    layout: {
      type: "tabs",
      inputs: "spaced",
      labels: "hidden",
    },
    variables: {
      fontLineHeight: "1.5",
      borderRadius: "20px",
      colorBackground: "oklch(32% 0.013 285.805)",
      accessibleColorOnColorPrimary: "#262626",
      spacingUnit: 8,
      tabSpacing: "5px",
    },
    rules: {
      ".Input": {
        backgroundColor: "oklch(32% 0.013 285.805)",
        padding: "6px 12px",
        border: "1px solid oklch(44.2% 0.017 285.786)",
        color: "oklch(93.382% 0.006 285.885)",
        fontSize: "14px",
      },
      ".Input:disabled, .Input--invalid:disabled": {
        color: "lightgray",
        backgroundColor: "#262626",
      },
      ".Tab": {
        padding: "10px 12px 8px 12px",
        border: "1px solid oklch(44.2% 0.017 285.786)",
        backgroundColor: "#262626",
        color: "oklch(93.382% 0.006 285.885)",
      },
      ".Tab:hover": {
        backgroundColor: "oklch(37% 0.013 285.805)",
        color: "oklch(93.382% 0.006 285.885)",
      },
      ".TabIcon": {
        width: "16px",
        fill: "oklch(70.888% 0.006 286.033)",
      },
      ".TabIcon:hover": {
        fill: "oklch(93.382% 0.006 285.885)",
      },
      ".Tab--selected, .Tab--selected:focus, .Tab--selected:hover": {
        border: "none",
        backgroundColor: "#007bfd",
        color: "oklch(93.382% 0.006 285.885)",
        boxShadow:
          "0 0 0 1.5px var(--colorPrimaryText), 0px 1px 1px rgba(0, 0, 0, 0.03), 0px 3px 7px rgba(18, 42, 66, 0.04)",
      },
      ".TabIcon--selected,.TabIcon--selected:hover,.TabIcon--selected:focus": {
        fill: "oklch(93.382% 0.006 285.885)",
      },
      ".Label": {
        fontSize: "14px",
        color: "oklch(70.888% 0.006 286.033)",
      },
    },
  };
};

declare const STRIPE_LIVE_PUBLIC_KEY: string;

const props = defineProps<{
  newsletter: Newsletter;
  emailAddress?: string;
}>();

const error = ref<string>("");
const stripe = ref<typeof window.Stripe | null>(null);
const email = ref<string>(props.emailAddress || "");
const elements = ref<any>(null);
const paymentElement = ref<any>(null);
const actions = ref<any>(null);
const amount = ref<number | null>(null);
const currency = ref<string>("");
const interval = ref<string>("");
const isMounting = ref<boolean>(true);
const isGift = ref<boolean>(false);
const giftRecipientEmailAddress = ref<string>("");
const giftMessage = ref<string>("");
const isPending = ref<boolean>(false);
const showSuccessModal = ref<boolean>(false);

const isButtonDisabled = computed(() => {
  if (!email.value) {
    return true;
  }
  if (isGift.value && !giftRecipientEmailAddress.value) {
    return true;
  }
  return false;
});

const formattedPrice = computed(() => {
  if (!amount.value || !currency.value) {
    return "";
  }
  const formatted = new Intl.NumberFormat("en-US", {
    style: "currency",
    currency: currency.value.toUpperCase(),
  }).format(amount.value / 100);

  if (interval.value) {
    return `${formatted}/${interval.value}`;
  }
  return formatted;
});

const archiveUrl = computed(() => {
  return `/${props.newsletter.username}/archive`;
});

const setupPayButton = () => {
  const payButton = document.getElementById("pay-button");
  payButton?.addEventListener("click", async () => {
    if (!actions.value) {
      return;
    }
    const confirmParams: any = {
      returnUrl: window.location.href,
      email: giftRecipientEmailAddress.value || email.value,
    };
    const { error } = await actions.value.confirm(confirmParams);
    if (error) {
      document.getElementById("confirm-errors")!.textContent = error.message;
    }
  });
};

const createCheckoutSession = async (emailAddress?: string) => {
  const body: any = {
    email_address: emailAddress,
    gift_message: giftMessage.value,
    gift_recipient_email_address: giftRecipientEmailAddress.value,
  };

  const response = await fetch(
    `/api/emails/api/newsletters/${props.newsletter.id}/checkout-sessions`,
    {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
        Authorization: `Token ${props.newsletter.id}`,
      },
      body: JSON.stringify(body),
    }
  );

  return await response.json();
};

const setupStripeElements = async () => {
  if (!stripe.value || !email.value) {
    return;
  }

  try {
    // Unmount existing payment element
    if (paymentElement.value) {
      paymentElement.value.unmount();
    }

    // Create new checkout session
    const {
      client_secret,
      amount_total,
      currency: currencyCode,
      metadata,
    } = await createCheckoutSession(email.value);

    amount.value = amount_total;
    currency.value = currencyCode;
    interval.value = metadata?.interval || "";

    // Create new elements with appearance
    elements.value = stripe.value.initCheckout({
      clientSecret: client_secret,
      elementsOptions: {
        appearance: getAppearance(),
      },
    });

    // Create and mount new payment element
    paymentElement.value = elements.value.createPaymentElement({
      layout: "tabs",
      terms: {
        card: "never",
      },
    });
    paymentElement.value.mount("#payment-element");

    const loadActionsResult = await elements.value.loadActions();
    if (loadActionsResult.error) {
      document.getElementById("confirm-errors")!.textContent =
        loadActionsResult.error.message;
    }
    actions.value = loadActionsResult.actions;

    setupPayButton();

    isMounting.value = false;
  } catch (err) {
    console.error("Error setting up Stripe elements:", err);
    error.value = "Failed to setup payment form.";
    isMounting.value = false;
  }
};

const initializePaymentElement = async () => {
  if (!window.Stripe) {
    return;
  }

  try {
    // TODO: Replace with actual publishable key from newsletter settings
    stripe.value = window.Stripe(STRIPE_LIVE_PUBLIC_KEY, {
      stripeAccount: props.newsletter.stripe_account,
    });
  } catch {
    error.value = "Failed to initialize payment form.";
    console.error("Stripe initialization error:", error);
  }

  await setupStripeElements();
};

const handleEmailChange = async () => {
  if (!email.value || !elements.value) {
    return;
  }

  try {
    // Update the email using Stripe's actions API
    const result = await elements.value.actions.updateEmail({
      email: email.value,
    });

    if (result.error) {
      console.error("Error updating email:", result.error);
      error.value = result.error.message || "Failed to update email.";
    }
  } catch (err) {
    console.error("Error updating email:", err);
    error.value = "Failed to update email.";
  }
};

const handleGiftToggle = async () => {
  if (!stripe.value || !email.value) {
    return;
  }

  isMounting.value = true;
  await setupStripeElements();
};

const handlegiftRecipientEmailAddressChange = async () => {
  if (!isGift.value || !giftRecipientEmailAddress.value || !stripe.value) {
    return;
  }

  isMounting.value = true;
  await setupStripeElements();
};

const handleGiftMessageChange = async () => {
  if (!isGift.value || !stripe.value) {
    return;
  }

  isMounting.value = true;
  await setupStripeElements();
};

const remountPaymentElement = async () => {
  if (!stripe.value || !email.value) {
    return;
  }

  isMounting.value = true;
  await setupStripeElements();
};

let observer: MutationObserver | null = null;

// Initialize Stripe
onMounted(async () => {
  // Check for successful payment return
  const urlParams = new URLSearchParams(window.location.search);
  const sessionId = urlParams.get("session_id");
  const accountId = urlParams.get("account_id");

  if (sessionId && accountId) {
    showSuccessModal.value = true;
    // Clean up the URL by removing query parameters
    const url = new URL(window.location.href);
    url.searchParams.delete("session_id");
    url.searchParams.delete("account_id");
    window.history.replaceState({}, "", url.toString());
  }

  // Load Stripe.js
  if (!window.Stripe) {
    const script = document.createElement("script");
    script.src = "https://js.stripe.com/clover/stripe.js";
    script.async = true;
    document.head.appendChild(script);
    await new Promise((resolve) => {
      script.onload = resolve;
    });
  }

  await initializePaymentElement();

  // Watch for dark class changes
  observer = new MutationObserver((mutations) => {
    mutations.forEach((mutation) => {
      if (
        mutation.type === "attributes" &&
        mutation.attributeName === "class"
      ) {
        remountPaymentElement();
      }
    });
  });

  observer.observe(document.documentElement, {
    attributes: true,
    attributeFilter: ["class"],
  });
});

onUnmounted(() => {
  if (observer) {
    observer.disconnect();
  }
});
</script>

<style lang="scss">
@import "./PaymentElement.scss";
</style>
